import React, {ReactElement} from 'react';
import {Main} from "./Main";
import {Box, Button, Typography} from "@mui/material";
import {FIREBASE_CONFIG} from "../consts";
import {
  AppConfig,
  BaseApp,
  BaseAppProps,
  BaseAppState, DisplayConfigRenderMode,
  MEDIUM_INSET0_DISPLAY_CONFIG,
  renderErrorCode
} from "../shared/BaseApp";
import {getAuth, User} from "@firebase/auth";
import {Account, AccountProfile, LoginCredentials, ProvisionedAppConfig} from "../shared/types";
import {Prefs} from "../shared/prefs";
import {EnterDeviceUnitId} from "./debug/EnterDeviceUnitId";
import {mediumGray} from "../shared/colors";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {SharedErrorCodes} from "../shared/constants";
import {StyledBoxColumn} from "../shared/StyledComponents";
import {loginWithEmailAndPassword} from "../shared/Login";
import {PathComponent} from "../index";
import Draggable from "react-draggable";
import Keyboard from "react-simple-keyboard";
import {appDomainNameString} from "../libwyze-common/types";
import {CloseOutlined, DragHandleOutlined} from "@mui/icons-material";
import {CheckinApi} from "../checkin-common/checkin_api";
import {CheckinErrorCodes} from "../checkin-common/types";
import {AppPrefs} from "./app_prefs";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {AccountProfileContextTrait, AccountTypeName, Pages} from "../libwyze-shared/types";
import {LibwyzeCommonInit} from "../libwyze-shared/init";
import {getMemberAuth} from "../shared/auth";
import {THEME} from "./theme";

const DEVICE_INFO_ID_PREF = new Prefs();

export function getDeviceUnitId(): string {
  return DEVICE_INFO_ID_PREF.getString("device_info_id");
}

export function setDeviceUnitId(deviceUnitId: string) {
  DEVICE_INFO_ID_PREF.setString("device_info_id", deviceUnitId);
}

export function clearDeviceUnitId() {
  DEVICE_INFO_ID_PREF.remove("device_info_id");
}

type SetupFragmentProps = BaseFragmentProps & {
  checkIntervalSeconds: number,
  setupCode: () => Promise<string>,
  doAutoLogin: () => Promise<LoginCredentials>,
}

type SetupFragmentState = BaseFragmentState & {
  autoLoggingIn?: boolean,
  setupCode: string,
  setupError: string,
}

class SetupFragment extends BaseFragment<SetupFragmentProps, SetupFragmentState> {

  private readonly auth = getAuth();
  private static readonly DEFAULT_CHECK_INTERVAL_SECONDS = 10;

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    let setupCode;
    try {
      const didLogin = await this.doAutoLogin();
      if (didLogin) {
        return;
      }
      setupCode = await this.props.setupCode();
    } catch (e) {
      // Continue.
    }
    this.setState({
      setupCode: setupCode,
    });
  }

  private scheduleAutoLogin() {
    setTimeout(() => this.doAutoLogin(), Math.max(this.props.checkIntervalSeconds, SetupFragment.DEFAULT_CHECK_INTERVAL_SECONDS) * 1000);
  }

  private async doAutoLogin(): Promise<boolean> {
    try {
      const loginCredentials = await this.props.doAutoLogin();
      console.log(`Got null creds, checking in ${SetupFragment.DEFAULT_CHECK_INTERVAL_SECONDS} secs`);
      if (loginCredentials) {
        loginWithEmailAndPassword(this.auth, loginCredentials.email, loginCredentials.password, (error) => this.setState({
          setupError: error,
        }))
          .then(() => window.location.href = "/");
        return true;
      }
    } catch (e) {
      // Continue.
    }
    this.scheduleAutoLogin();
    return false;
  }

  render(): ReactElement {
    if (!this.state.ready || !this.state.setupCode) {
      return this.renderPleaseWait();
    }
    if (!this.state.setupCode) {
      return renderErrorCode(SharedErrorCodes.ERROR_LOGIN_NO_SETUP_CODE);
    }
    const code = this.state.setupCode;
    const formatted = code.slice(0, 3) + " - " + code.slice(3, 6) + " - " + code.slice(6);
    return <StyledBoxColumn
      style={{flexGrow: 1, alignItems: "center", justifyContent: "center", gap: 48, position: "relative"}}>
      <Typography variant="h3">{formatted}</Typography>
      <Typography style={{textAlign: "center"}}>Link this device with the Checkin Manager
        app.<br/>Visit <b>{`https://checkin-manager.${appDomainNameString()}`}</b></Typography>
      {/*{ENV === Environment.DEBUG ?*/}
      {/*  <Button variant="text" style={{position: "absolute", right: PD_MD, bottom: PD_MD, padding: PD_SM}}*/}
      {/*          onClick={() => this.props.setView(View.LOGIN)}>*/}
      {/*    Log in or Sign up*/}
      {/*  </Button>*/}
      {/*  : null}*/}
    </StyledBoxColumn>;
  }
}

export class AppRefs {

  constructor() {
  }
}

export type AppProps = BaseAppProps & {}

type AppState = BaseAppState & {}

export class App extends ProvisionedApp<AppProps, AppState> implements AccountProfileContextTrait{

  private static appRefs: AppRefs;

  static getAppRefs(): AppRefs {
    if (!App.appRefs) {
      App.appRefs = App.createAppRefs();
    }
    return App.appRefs;
  }

  static nestedPaths(): PathComponent[] {
    return [];
  }

  private static createAppRefs() {
    return new AppRefs();
  }

  protected async onAppMounted(): Promise<void> {
    await this.updateDeviceUnitId();
    return super.onAppMounted();
  }

  private async updateDeviceUnitId(): Promise<void> {
    let currentDeviceUnitId = getDeviceUnitId();
    const search = window.location.search;
    if (search) {
      const params = new URLSearchParams(search.substring(1));
      const deviceUnitId = params.get("fiid") as string;
      if (deviceUnitId && deviceUnitId !== currentDeviceUnitId) {
        const auth = getAuth();
        await auth.signOut();
        setDeviceUnitId(deviceUnitId);
      }
    }
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: "Checkin",
      icon: "/icon.png",
      logo: "/logo192.png",
      stamp: "/logo192.png",
      theme: THEME,
      defaultUserImage: "/images/default_user.png",
      loginConfig: {
        provisioning: {
          setupRequired: true,
          renderSetup: (provisioningId, onSetupComplete) => {
            return <SetupFragment
              setupCode={() => this.fetchSetupCode()}
              doAutoLogin={() => this.fetchDeviceLogin()}
              checkIntervalSeconds={5}/>;
          },
        },
      },
      firebaseConfig: {options: FIREBASE_CONFIG},
      displayConfig: MEDIUM_INSET0_DISPLAY_CONFIG.setRenderMode(DisplayConfigRenderMode.AUTOZOOM),
    };
  }

  getProvisionedAppId(): string {
    return "dentwyze";
  }

  getProvisionedAppConfig(): ProvisionedAppConfig | null {
    return undefined;
  }


  private account: Account;
  private accountProfile: AccountProfile;

  protected async onAppInitWithLoggedInUser(user: User): Promise<void> {
    await super.onAppInitWithLoggedInUser(user);
    await LibwyzeCommonInit.init(user);

    const memberAuth = getMemberAuth();
    this.account = await memberAuth.getOrLoadAccountByType(AccountTypeName.SERVICE);
    this.accountProfile = await this.account?.getOrLoadProfile();
    if (this.accountProfile?.type === AccountTypeName.PROVIDER) {
      const practicePageId = this.getPracticePageId();
      if (practicePageId) {
        const page = await Pages.getInstance().getOrLoadItem(practicePageId);
      }
    }
  }

  getAccountProfile<T extends AccountProfile>(): T {
    return this.accountProfile as T;
  }

  getPracticePageId(): string | undefined {
    return AppPrefs.getInstance().getSelectedPracticeId();
  }

  getPatientLinkedPracticePageIds(): string[] {
    return [];
  }

  getProviderClaimedPracticePageIds(): string[] {
    return [];
  }

  getProviderPageId(): string | undefined {
    return undefined;
  }

  private async fetchSetupCode() {
    return getDeviceUnitId();
  }

  private async fetchDeviceLogin(): Promise<LoginCredentials> {
    const deviceLogin = await CheckinApi.getInstance().devicesLogin(getDeviceUnitId());
    if (!deviceLogin) {
      return null;
    }
    AppPrefs.getInstance().setSelectedPracticeId(deviceLogin.practiceId);
    return {
      email: deviceLogin.email,
      password: deviceLogin.password,
    } as LoginCredentials;
  }

  protected renderLogin(): ReactElement {
    const inputSkuIdIfAbsent = true;
    if (!getDeviceUnitId()) {
      if (inputSkuIdIfAbsent) {
        return <EnterDeviceUnitId/>;
      } else {
        return renderErrorCode(CheckinErrorCodes.ERROR_NO_SKU_ID);
      }
    }
    return super.renderLogin();
  }

  protected renderSoftKeyboard(): ReactElement {
    return <Draggable>
      <div style={{
        zIndex: 1200,
        position: "absolute",
        display: "flex",
        flexDirection: "column",
        left: 16,
        right: 16,
        bottom: 16,
        padding: 8,
        gap: 8,
        background: mediumGray,
      }}>
        <Box style={{display: "flex"}}>
          <DragHandleOutlined style={{fontSize: 32, color: "white", padding: 8}}/>
          <span style={{flexGrow: 1}}/>
          <Button variant="text" onClick={() => BaseApp.CONTEXT.hideSoftKeyboard()}>
            <CloseOutlined style={{fontSize: 32, color: "white", padding: 8}}/>
          </Button>
        </Box>
        <Keyboard/>
      </div>
    </Draggable>;
  }

  protected renderMain(): ReactElement {
    return <Main/>;
  }
}